/** Cinq gabarits A4 — devis / facture / avoir. */
export type PageAlign = "left" | "center" | "right";

export const MODEL_SKINS = [
  { id: "aurelia", label: "Aurélia" },
  { id: "classique", label: "Classique" },
  { id: "bandeau", label: "Bandeau" },
  { id: "encre", label: "Encre" },
  { id: "atelier", label: "Atelier" },
] as const;

export type ModelSkin = (typeof MODEL_SKINS)[number]["id"];

export type CompanyModel = {
  skin: ModelSkin;
  color_primary: string;
  color_secondary: string;
  /** Vide = fond d’origine du modèle choisi. */
  color_block: string;
  font_size: number;
  logo_pct: number;
  paid_stamp: boolean;
  ship_address: boolean;
  page_numbers: boolean;
  brand_mark: boolean;
  show_issuer_phone: boolean;
  show_issuer_email: boolean;
  show_issuer_iban: boolean;
  show_client_phone: boolean;
  show_client_email: boolean;
  show_client_contacts: boolean;
  page_align: PageAlign;
};

function asBool(v: unknown, fallback: boolean): boolean {
  if (v == null) return fallback;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (v === "0" || v === "false") return false;
  return Boolean(v);
}

function asHex(v: unknown, fallback: string): string {
  const s = String(v ?? "").trim().replace(/^#/, "");
  return /^[0-9a-fA-F]{6}$/.test(s) ? `#${s.toLowerCase()}` : fallback;
}

function asInt(v: unknown, fallback: number, min: number, max: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.trunc(n);
  return i < min || i > max ? fallback : i;
}

function asSkin(v: unknown): ModelSkin {
  const s = String(v ?? "");
  return MODEL_SKINS.some((x) => x.id === s) ? (s as ModelSkin) : "aurelia";
}

export function modelFromCompany(row: Partial<CompanyModel> | null | undefined): CompanyModel {
  const align = row?.page_align;
  return {
    skin: asSkin(row?.skin),
    color_primary: asHex(row?.color_primary, "#4f46e5"),
    color_secondary: asHex(row?.color_secondary, "#475569"),
    color_block: asHex(row?.color_block, ""),
    font_size: asInt(row?.font_size, 12, 11, 16),
    logo_pct: asInt(row?.logo_pct, 100, 60, 100),
    paid_stamp: asBool(row?.paid_stamp, true),
    ship_address: asBool(row?.ship_address, true),
    page_numbers: asBool(row?.page_numbers, true),
    brand_mark: asBool(row?.brand_mark, true),
    show_issuer_phone: asBool(row?.show_issuer_phone, true),
    show_issuer_email: asBool(row?.show_issuer_email, true),
    show_issuer_iban: asBool(row?.show_issuer_iban, true),
    show_client_phone: asBool(row?.show_client_phone, true),
    show_client_email: asBool(row?.show_client_email, true),
    show_client_contacts: asBool(row?.show_client_contacts, true),
    page_align: align === "left" || align === "center" || align === "right" ? align : "right",
  };
}

/** Fond des blocs. Vide en base = couleur d’origine du modèle affiché. */
export function modelBlockColor(model: Pick<CompanyModel, "skin" | "color_primary" | "color_block">): string {
  if (/^#[0-9a-fA-F]{6}$/.test(model.color_block)) return model.color_block.toLowerCase();
  if (model.skin === "encre") return "#111827";
  if (model.skin === "bandeau") return model.color_primary;
  if (model.skin === "classique") return "#ffffff";
  return "#f3f5fb";
}

export function modelBlockInk(hex: string): string {
  const h = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return "#1e1b4b";
  const n = parseInt(h, 16);
  const y = (((n >> 16) & 255) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000;
  return y > 160 ? "#1e1b4b" : "#ffffff";
}
